export type ErrorPhase = 'silent' | 'warning' | 'error';

export interface ErrorPhaseManagerOptions {
  /** ms before silent → warning */
  phase1Duration: number;
  /** ms before warning → error */
  phase2Duration: number;
  onPhaseChange?: (phase: ErrorPhase) => void;
}

/**
 * Reusable timer-based state machine for 3-phase error escalation.
 *
 * Phase progression: silent → warning → error
 * Call start() when an error is detected, reset() when recovered.
 */
export class ErrorPhaseManager {
  private phase: ErrorPhase = 'silent';
  private timer1: ReturnType<typeof setTimeout> | null = null;
  private timer2: ReturnType<typeof setTimeout> | null = null;
  private active = false;
  private options: ErrorPhaseManagerOptions;

  constructor(options: ErrorPhaseManagerOptions) {
    this.options = options;
  }

  /** Begin phase progression from silent → warning → error */
  start(): void {
    if (this.active) return;
    this.active = true;
    this.phase = 'silent';

    this.timer1 = setTimeout(() => {
      this.phase = 'warning';
      this.options.onPhaseChange?.('warning');

      this.timer2 = setTimeout(() => {
        this.phase = 'error';
        this.options.onPhaseChange?.('error');
      }, this.options.phase2Duration);
    }, this.options.phase1Duration);
  }

  /** Clear all timers and reset to silent */
  reset(): void {
    if (this.timer1) {
      clearTimeout(this.timer1);
      this.timer1 = null;
    }
    if (this.timer2) {
      clearTimeout(this.timer2);
      this.timer2 = null;
    }
    this.phase = 'silent';
    this.active = false;
  }

  getPhase(): ErrorPhase {
    return this.phase;
  }

  isActive(): boolean {
    return this.active;
  }
}
